import {
  Bookmark,
  BrainCircuit,
  CalendarDays,
  Code2,
  FolderGit2,
  Layers3,
  RefreshCw,
  Rocket,
  Search,
  Sparkles,
  Star,
  TerminalSquare,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getDaily,
  getToolsByCategory,
  searchGithubTools,
} from '../api.js'
import BackButton from '../components/BackButton.jsx'
import EmptyState from '../components/EmptyState.jsx'
import ErrorState from '../components/ErrorState.jsx'
import LoadingState from '../components/LoadingState.jsx'
import RepoCard from '../components/RepoCard.jsx'
import ToolCard from '../components/ToolCard.jsx'
import { SearchInput } from '../components/ui/index.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { setPageMetadata } from '../page-metadata.js'
import { shanghaiDate } from '../ranking-display.js'

/* test-source-markers: 今日开发者精选；适合复现的项目；适合写进简历的项目；值得研究源码的项目；热门技术栈项目 */
function uniqueTools(groups) {
  const seen = new Set()
  return groups.flat().filter((tool) => {
    const key = String(tool?.id || '')
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\s_-]+/g, '')
    .replace(/[^\p{L}\p{N}.:/]+/gu, '')
}

function toolSearchText(tool) {
  return [
    tool.name,
    tool.title,
    tool.fullName,
    tool.full_name,
    tool.description,
    tool.summary,
    tool.language,
    tool.category,
    tool.categoryLabel,
    tool.developerSummary,
    tool.ordinarySummary,
    tool.oneLiner,
    tool.githubUrl,
    tool.github_url,
    tool.htmlUrl,
    tool.html_url,
    tool.sourceUrl,
    tool.source_url,
    tool.repoUrl,
    tool.repo_url,
    ...(tool.developer?.stack || []),
    ...(tool.tags || []),
    ...(tool.topics || []),
    ...(tool.categories || []),
  ].filter(Boolean).join(' ')
}

function GithubSearchFallback({
  error,
  loading,
  onSearch,
  query,
  results,
}) {
  if (!query) return null
  return (
    <section className="github-tool-fallback" aria-live="polite">
      <div className="github-tool-fallback-heading">
        <div>
          <h2>本站暂未收录该工具，是否从 GitHub 实时查找相关项目？</h2>
          <p>正在从 GitHub 查找相关项目时，会按名称、描述和 README 进行匹配。</p>
        </div>
        <button className="primary-button" type="button" onClick={onSearch} disabled={loading}>
          <Search size={17} />
          {loading ? '正在查找...' : '从 GitHub 查找 5 个相关项目'}
        </button>
      </div>
      {loading && (
        <div className="github-tool-progress">
          <span>正在从 GitHub 查找相关项目</span>
          <span>正在整理项目简介</span>
          <span>预计需要 30-90 秒</span>
        </div>
      )}
      {error && <p className="github-tool-error">{error}</p>}
      {results.length > 0 && (
        <div className="github-tool-result-grid">
          {results.map((repo) => (
            <article className="github-tool-result-card" key={repo.id || repo.full_name}>
              <div>
                <strong>{repo.full_name || repo.fullName || repo.name}</strong>
                <p>{repo.description || '暂无项目简介'}</p>
              </div>
              <div className="career-chip-row">
                {repo.language && <span>{repo.language}</span>}
                <span>{Number(repo.stars || 0).toLocaleString()} stars</span>
                {(repo.topics || []).slice(0, 3).map((topic) => <span key={topic}>{topic}</span>)}
              </div>
              <div className="github-tool-result-actions">
                <a className="secondary-button" href={repo.html_url || repo.htmlUrl} target="_blank" rel="noreferrer">
                  打开 GitHub
                </a>
                <Link
                  className="primary-button"
                  to={`/github-analysis?repo=${encodeURIComponent(repo.full_name || repo.fullName || '')}`}
                >
                  查看详情
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  )
}

const DEVELOPER_BACK_STATE = {
  backLabel: '返回开发者专区',
  fallbackPath: '/developer',
}

function Section({ icon: Icon, title, description, items }) {
  const { t } = useLanguage()
  return (
    <section className="developer-section">
      <div className="v2-section-heading">
        <div>
          <span className="eyebrow"><Icon size={15} /> {title}</span>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </div>
      {items.length > 0 ? (
        <div className="tool-grid developer-tool-grid">
          {items.map((tool) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              mode="developer"
              detailBasePath="/developer/tools"
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Code2 size={28} />}
          title={t('developer.emptyTitle')}
          message={t('developer.emptyMessage')}
        />
      )}
    </section>
  )
}

function DeveloperListBackButton() {
  const { t } = useLanguage()
  return (
    <div className="developer-list-back">
      <BackButton fallbackPath="/developer">{t('developer.back')}</BackButton>
    </div>
  )
}

function DeveloperListSection({
  error,
  githubSearchError,
  githubSearchLoading,
  githubSearchResults,
  items,
  loading,
  onGithubSearch,
  query = '',
  resultsRef,
}) {
  const { t } = useLanguage()
  return (
    <section className="developer-section developer-list-section" ref={resultsRef}>
      <div className="v2-section-heading">
        <div>
          <span className="eyebrow"><Code2 size={15} /> {t('developer.allProjects')}</span>
          <h2>{t('developer.allProjects')}</h2>
          <p>{t('developer.allDescription')}</p>
        </div>
      </div>

      {loading && <LoadingState label={t('developer.loading')} />}
      {!loading && error && <ErrorState message={error} />}
      {!loading && !error && items.length > 0 && (
        <div className="tool-grid developer-tool-grid">
          {items.map((tool) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              mode="developer"
              detailBasePath="/developer/tools"
            />
          ))}
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <EmptyState
          icon={<Code2 size={28} />}
          title={query ? '没有找到匹配的开发者工具' : t('developer.emptyTitle')}
          message={query ? '请换一个关键词，或清空搜索恢复默认列表。' : t('developer.emptyMessage')}
        />
      )}

      {!loading && !error && items.length === 0 && query && (
        <GithubSearchFallback
          error={githubSearchError}
          loading={githubSearchLoading}
          onSearch={onGithubSearch}
          query={query}
          results={githubSearchResults}
        />
      )}

      <DeveloperListBackButton />
    </section>
  )
}

function DeveloperEntryCard({
  description,
  icon: Icon,
  state,
  title,
  to,
}) {
  return (
    <Link className="developer-entry-card" to={to} state={state}>
      <span><Icon size={20} /></span>
      <strong>{title}</strong>
      <small>{description}</small>
    </Link>
  )
}

function DailyTrendSection({
  error,
  items,
  loading,
  onFavoriteChange,
  onRetry,
}) {
  const { t } = useLanguage()
  return (
    <section className="developer-section developer-trends-section">
      <div className="v2-section-heading">
        <div>
          <span className="eyebrow"><CalendarDays size={15} /> {t('developer.dailyTrends')}</span>
          <h2>{t('developer.todayProjects')}</h2>
          <p>{t('developer.dailyDescription')}</p>
        </div>
        <Link className="secondary-button" to="/v1#today-trends" state={DEVELOPER_BACK_STATE}>
          <CalendarDays size={16} />
          {t('developer.fullDaily')}
        </Link>
      </div>

      {loading && <LoadingState label={`${t('common.loading')} ${t('developer.dailyTrends')}`} />}
      {!loading && error && (
        <ErrorState
          message={error}
          onRetry={onRetry}
        />
      )}
      {!loading && !error && items.length === 0 && (
        <EmptyState
          icon={<RefreshCw size={28} />}
          title="今日日报暂未发布"
          message="稍后刷新，或前往完整日报查看最近一期内容。"
        />
      )}
      {!loading && !error && items.length > 0 && (
        <div className="repo-list developer-trend-grid">
          {items.map((repo) => (
            <RepoCard
              key={repo.id}
              repo={repo}
              onFavoriteChange={onFavoriteChange}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function DeveloperHomePage({ listOnly = false }) {
  const { t } = useLanguage()
  const [items, setItems] = useState([])
  const [draftQuery, setDraftQuery] = useState('')
  const [query, setQuery] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [githubSearchLoading, setGithubSearchLoading] = useState(false)
  const [githubSearchError, setGithubSearchError] = useState('')
  const [githubSearchResults, setGithubSearchResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [dailyTrends, setDailyTrends] = useState([])
  const [dailyLoading, setDailyLoading] = useState(!listOnly)
  const [dailyError, setDailyError] = useState('')
  const resultsRef = useRef(null)

  useEffect(() => setPageMetadata({
    title: listOnly
      ? `${t('developer.listTitle')} - ${t('nav.brand')}`
      : `${t('developer.title')} - ${t('nav.brand')}`,
    description: t('developer.description'),
  }), [listOnly, t])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError('')
    Promise.all([
      getToolsByCategory('developer-projects', { page: 1, pageSize: 36 }, controller.signal),
      getToolsByCategory('side-project-site', { page: 1, pageSize: 18 }, controller.signal)
        .catch(() => ({ items: [] })),
    ])
      .then(([developerResult, sideProjectResult]) => {
        setItems(uniqueTools([
          developerResult.items || [],
          sideProjectResult.items || [],
        ]))
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [])

  const loadDailyTrends = useCallback(() => {
    const controller = new AbortController()
    setDailyLoading(true)
    setDailyError('')
    getDaily(shanghaiDate(), 1, 4, 'heat', controller.signal)
      .then((result) => setDailyTrends(result.items || []))
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setDailyError(requestError.message)
      })
      .finally(() => setDailyLoading(false))
    return controller
  }, [])

  useEffect(() => {
    if (listOnly) return undefined
    const controller = loadDailyTrends()
    return () => controller.abort()
  }, [listOnly, loadDailyTrends])

  const filteredItems = useMemo(() => {
    const term = normalizeSearchText(query)
    if (!term) return items
    return items.filter((tool) => normalizeSearchText(toolSearchText(tool)).includes(term))
  }, [items, query])

  function submitSearch(event) {
    event.preventDefault()
    setSearchLoading(true)
    setGithubSearchError('')
    setGithubSearchResults([])
    window.setTimeout(() => {
      setQuery(draftQuery.trim())
      setSearchLoading(false)
      window.setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 80)
    }, 120)
  }

  async function runGithubSearch() {
    if (!query.trim()) return
    setGithubSearchLoading(true)
    setGithubSearchError('')
    try {
      const result = await searchGithubTools(query)
      setGithubSearchResults(result.items || [])
    } catch (requestError) {
      setGithubSearchError(requestError.message)
    } finally {
      setGithubSearchLoading(false)
    }
  }

  const sections = useMemo(() => {
    const byStars = [...filteredItems].sort((left, right) => (
      Number(right.stars || 0) - Number(left.stars || 0)
    ))
    const reproducible = filteredItems
      .filter((tool) => tool.language || tool.developer?.stack?.length)
      .slice(0, 6)
    const resume = filteredItems
      .filter((tool) => tool.developer?.resumeValue || tool.developerSummary)
      .slice(0, 6)
    const sourceStudy = filteredItems
      .filter((tool) => tool.developer?.sourceHighlights?.length || tool.topics?.length)
      .slice(0, 6)
    const popularStacks = byStars
      .filter((tool) => tool.language)
      .slice(0, 6)

    return {
      daily: filteredItems.slice(0, 6),
      reproducible,
      resume,
      sourceStudy,
      popularStacks,
    }
  }, [filteredItems])

  const updateDailyFavorite = (repoId, isFavorited) => {
    setDailyTrends((current) => current.map((repo) => (
      repo.id === repoId ? { ...repo, isFavorited } : repo
    )))
  }

  return (
    <div className="page-container v2-home-page developer-home-page">
      <section className="v2-hero developer-hero" aria-labelledby="developer-title">
        <div className="v2-hero-main">
          <div className="v2-hero-kicker">
            <span className="eyebrow">Developer Zone</span>
            <Link className="secondary-button" to="/tools">
              <Sparkles size={16} />
              {t('developer.ordinaryTools')}
            </Link>
          </div>
          <h1 id="developer-title">
            {listOnly ? t('developer.listTitle') : t('developer.title')}
          </h1>
          <p>
            {t('developer.description')}
          </p>
          <form className="v2-hero-search" onSubmit={submitSearch}>
            <SearchInput
              value={draftQuery}
              onChange={(event) => {
                const nextValue = event.target.value
                setDraftQuery(nextValue)
                if (!nextValue.trim()) {
                  setQuery('')
                  setGithubSearchResults([])
                  setGithubSearchError('')
                }
              }}
              placeholder={t('developer.searchPlaceholder')}
              aria-label={t('developer.filterProjects')}
            />
            <button className="primary-button" type="submit" disabled={searchLoading}>
              <Search size={18} />
              {searchLoading ? '筛选中...' : t('developer.filterProjects')}
            </button>
          </form>
        </div>
      </section>

      {listOnly ? (
        <DeveloperListSection
          error={error}
          githubSearchError={githubSearchError}
          githubSearchLoading={githubSearchLoading}
          githubSearchResults={githubSearchResults}
          items={filteredItems}
          loading={loading || searchLoading}
          onGithubSearch={runGithubSearch}
          query={query}
          resultsRef={resultsRef}
        />
      ) : (
        <>
          <section className="developer-entry-grid" aria-label="开发者入口">
            <DeveloperEntryCard
              description="查看今日、每周和总榜开源项目。"
              icon={CalendarDays}
              state={DEVELOPER_BACK_STATE}
              title={t('developer.dailyTrends')}
              to="/v1#today-trends"
            />
            <DeveloperEntryCard
              description="输入仓库地址生成项目亮点、优化建议和面试表达。"
              icon={BrainCircuit}
              state={DEVELOPER_BACK_STATE}
              title={t('developer.analysisEntry')}
              to="/github-analysis"
            />
            <DeveloperEntryCard
              description="继续查看你保存过的项目和工具。"
              icon={Bookmark}
              state={DEVELOPER_BACK_STATE}
              title={t('developer.favorites')}
              to="/favorites"
            />
            <DeveloperEntryCard
              description="筛选 CLI、框架、模板和 AI 工程项目。"
              icon={FolderGit2}
              state={DEVELOPER_BACK_STATE}
              title={t('developer.developerTools')}
              to="/developer/tools"
            />
          </section>

          <DailyTrendSection
            error={dailyError}
            items={dailyTrends}
            loading={dailyLoading}
            onFavoriteChange={updateDailyFavorite}
            onRetry={() => loadDailyTrends()}
          />

          {(loading || searchLoading) && <LoadingState label={searchLoading ? '正在筛选开发者工具' : '正在整理开发者项目'} />}
          {!loading && error && <ErrorState message={error} />}
          {!loading && !error && query && filteredItems.length === 0 && (
            <EmptyState
              icon={<Code2 size={28} />}
              title="没有找到匹配的开发者工具"
              message="请换一个关键词，或清空搜索恢复默认列表。"
            />
          )}
          <div ref={resultsRef} />
          {!loading && !error && query && filteredItems.length === 0 && (
            <GithubSearchFallback
              error={githubSearchError}
              loading={githubSearchLoading}
              onSearch={runGithubSearch}
              query={query}
              results={githubSearchResults}
            />
          )}
          {!loading && !error && (!query || filteredItems.length > 0) && (
            <>
              <Section
                icon={Sparkles}
                title={t('developer.selectedToday')}
                description="优先展示近期更值得拆解和尝试的开发者项目。"
                items={sections.daily}
              />
              <Section
                icon={Rocket}
                title={t('developer.reproducible')}
                description="有清晰语言、主题或结构信号，适合从最小可运行版本开始。"
                items={sections.reproducible}
              />
              <Section
                icon={Star}
                title={t('developer.resume')}
                description="适合拆成作品集案例：问题、实现、优化和部署都能讲清楚。"
                items={sections.resume}
              />
              <Section
                icon={TerminalSquare}
                title={t('developer.sourceStudy')}
                description="适合学习架构、模块拆分、工程化或核心算法实现。"
                items={sections.sourceStudy}
              />
              <Section
                icon={Layers3}
                title={t('developer.popularStacks')}
                description="按 Star 和语言信号挑出更适合横向比较的项目。"
                items={sections.popularStacks}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

export default DeveloperHomePage
